import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  Paper,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Avatar,
  Snackbar,
  Alert,
  Button,
  Tabs,
  Tab,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { Play, Clock, MessageSquare, Paperclip, GitBranch, Link2, AlertCircle, Bug, Lightbulb, Zap, CheckSquare, HelpCircle, ArrowUpRight, Tag, Lock, Calendar, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync, stopTimerAsync, clearTimerForTask } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { updateTaskStatus } from '@/store/slices/tasksSlice';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus, TaskPriority, IssueType } from '@/types';
import { getCategoryStyle } from '@/shared/utils/categoryStyles';
import PriorityBadge from '@/shared/components/PriorityBadge';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { canComplete, canDoWork } from '@/shared/components/TaskTable';
import { useTodaysPickedTaskIds } from '@/hooks/useTodaysPickedTaskIds';
import { PICK_BEFORE_TIMER_MESSAGE } from '@/shared/constants/timerRules';

interface TaskKanbanViewProps {
  tasks: Task[];
  isLoading: boolean;
  onStatusChange?: (taskId: string, status: TaskStatus) => void;
}

// Column configuration
const columns = [
  { id: TaskStatus.NOT_STARTED, title: 'Not Started', color: '#64748b' },
  { id: TaskStatus.IN_PROGRESS, title: 'In Progress', color: '#2563eb' },
  { id: TaskStatus.BLOCKED, title: 'Blocked', color: '#ef4444' },
  { id: TaskStatus.COMPLETED, title: 'Completed', color: '#16a34a' },
];

// Get icon for issue type
const getIssueTypeIcon = (type: IssueType): React.ReactElement | undefined => {
  switch (type) {
    case 'bug': return <Bug size={10} style={{ color: '#ef4444' }} />;
    case 'feature': return <Lightbulb size={10} style={{ color: '#10b981' }} />;
    case 'improvement': return <Zap size={10} style={{ color: '#f59e0b' }} />;
    case 'task': return <CheckSquare size={10} style={{ color: '#3b82f6' }} />;
    case 'question': return <HelpCircle size={10} style={{ color: '#6b7280' }} />;
    default: return undefined;
  }
};

// Get issue type styling
const getIssueTypeStyle = (type: IssueType) => {
  switch (type) {
    case 'bug': return { bgcolor: '#fee2e2', color: '#ef4444', label: 'Bug' };
    case 'feature': return { bgcolor: '#d1fae5', color: '#10b981', label: 'Feature' };
    case 'improvement': return { bgcolor: '#fef3c7', color: '#f59e0b', label: 'Improvement' };
    case 'task': return { bgcolor: '#dbeafe', color: '#3b82f6', label: 'Task' };
    case 'question': return { bgcolor: '#f3f4f6', color: '#6b7280', label: 'Question' };
    default: return { bgcolor: '#f3f4f6', color: '#6b7280', label: type };
  }
};

/**
 * TaskKanbanView
 * Drag-and-drop kanban board for task status management
 * Mobile: Tab-based column navigation with swipeable cards
 * Desktop: Traditional multi-column drag-and-drop
 * Follows GTD methodology with status-based columns
 */
const TaskKanbanView: React.FC<TaskKanbanViewProps> = ({
  tasks,
  isLoading,
  onStatusChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);
  const todaysPickedTaskIds = useTodaysPickedTaskIds();
  const [confirmCompleteTask, setConfirmCompleteTask] = useState<Task | null>(null);
  const [timerPickError, setTimerPickError] = useState('');

  /** Check if current user can use timer (ONLY Responsible users) */
  const canUseTimer = (task: Task): boolean => {
    const userId = user?.id;
    if (!userId) return false;
    return task.raci?.responsible?.includes(userId) || false;
  };

  const [activeTab, setActiveTab] = useState(0);

  const [blockingWarning, setBlockingWarning] = useState<{
    open: boolean;
    taskId: string | null;
    message: string;
    blockingTaskIds: string[];
  }>({
    open: false,
    taskId: null,
    message: '',
    blockingTaskIds: [],
  });

  // Group tasks by status
  const getTasksByStatus = useCallback(() => {
    const grouped: Record<TaskStatus, Task[]> = {
      [TaskStatus.NOT_STARTED]: [],
      [TaskStatus.IN_PROGRESS]: [],
      [TaskStatus.BLOCKED]: [],
      [TaskStatus.COMPLETED]: [],
      [TaskStatus.CANCELLED]: [],
    };

    tasks.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });

    return grouped;
  }, [tasks]);

  const tasksByStatus = getTasksByStatus();

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId as TaskStatus;
    const task = tasks.find((t) => t.id === draggableId);
    
    if (!task || !user) return;

    if (newStatus === TaskStatus.COMPLETED && !canComplete(task, user)) {
      return;
    }
    if (newStatus !== TaskStatus.COMPLETED && task.status === TaskStatus.COMPLETED && !canDoWork(task, user)) {
      return;
    }
    if (newStatus !== TaskStatus.COMPLETED && task.status !== TaskStatus.COMPLETED && !canDoWork(task, user)) {
      return;
    }
    
    // If moving to COMPLETED, validate dependencies
    if (newStatus === TaskStatus.COMPLETED) {
      // Check for incomplete blocking dependencies
      const blockedByIds = task.blockedByTaskIds || [];
      const incompleteBlockers = blockedByIds.filter((id) => {
        const blockingTask = tasks.find((t) => t.id === id);
        return blockingTask && 
          blockingTask.status !== TaskStatus.COMPLETED && 
          blockingTask.status !== TaskStatus.CANCELLED;
      });
      
      if (incompleteBlockers.length > 0) {
        const blockerNames = incompleteBlockers
          .map((id) => tasks.find((t) => t.id === id)?.title || 'Unknown task')
          .slice(0, 3);
        const moreCount = incompleteBlockers.length - 3;
        const message = `Cannot complete "${task.title}": Blocked by ${blockerNames.join(', ')}${moreCount > 0 ? ` and ${moreCount} more` : ''}`;
        
        setBlockingWarning({
          open: true,
          taskId: task.id,
          message,
          blockingTaskIds: incompleteBlockers,
        });
        return;
      }
      
      // Check for incomplete subtasks
      const subtaskIds = task.subtaskIds || [];
      const incompleteSubtasks = subtaskIds.filter((id) => {
        const subtask = tasks.find((t) => t.id === id);
        return subtask && 
          subtask.status !== TaskStatus.COMPLETED && 
          subtask.status !== TaskStatus.CANCELLED;
      });
      
      if (incompleteSubtasks.length > 0) {
        setBlockingWarning({
          open: true,
          taskId: task.id,
          message: `Cannot complete "${task.title}": ${incompleteSubtasks.length} subtask(s) are still incomplete`,
          blockingTaskIds: incompleteSubtasks,
        });
        return;
      }
      setConfirmCompleteTask(task);
      return;
    }

    const previousStatus = task.status;
    dispatch(updateTaskStatus({ taskId: draggableId, status: newStatus }));
    onStatusChange?.(draggableId, newStatus);
    taskService
      .updateTaskStatus(draggableId, newStatus)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      })
      .catch(() => {
        dispatch(updateTaskStatus({ taskId: draggableId, status: previousStatus }));
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      });
  };
  
  const handleViewBlockingTasks = () => {
    if (blockingWarning.blockingTaskIds.length > 0) {
      navigate(`/tasks?taskId=${blockingWarning.blockingTaskIds[0]}`);
    }
    setBlockingWarning({ open: false, taskId: null, message: '', blockingTaskIds: [] });
  };

  const handleCloseWarning = () => {
    setBlockingWarning({ open: false, taskId: null, message: '', blockingTaskIds: [] });
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  const handleStartTimer = (e: React.MouseEvent, taskId: string, taskTitle: string) => {
    e.stopPropagation();
    if (!todaysPickedTaskIds.has(taskId)) {
      setTimerPickError(PICK_BEFORE_TIMER_MESSAGE);
      return;
    }
    if (user) {
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle }));
    }
  };

  // Get deadline styling
  const getDeadlineColor = (dueDate: string, status: TaskStatus) => {
    if (status === TaskStatus.COMPLETED) return 'text.secondary';
    const now = dayjs();
    const deadline = dayjs(dueDate);
    const hoursUntil = deadline.diff(now, 'hour');

    if (hoursUntil < 0) return 'error.main';
    if (hoursUntil < 24) return 'warning.main';
    return 'text.secondary';
  };

  if (isLoading) {
    // Mobile loading skeleton
    if (isMobile) {
      return (
        <Box>
          {/* Tab skeleton */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, overflow: 'auto' }}>
            {columns.map((col) => (
              <Skeleton key={col.id} variant="rounded" width={80} height={32} />
            ))}
          </Box>
          {/* Card skeletons */}
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 1.5 }} />
          ))}
        </Box>
      );
    }
    
    // Desktop loading skeleton
    return (
      <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', pb: 2 }}>
        {columns.map((column) => (
          <Paper
            key={column.id}
            sx={{
              minWidth: 300,
              maxWidth: 300,
              bgcolor: 'background.default',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: column.color,
                }}
              />
              <Skeleton variant="text" width={100} />
            </Box>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={120}
                sx={{ mb: 1.5 }}
              />
            ))}
          </Paper>
        ))}
      </Box>
    );
  }

  // Mobile Kanban View (Tab-based)
  if (isMobile) {
    const activeColumn = columns[activeTab];
    const columnTasks = tasksByStatus[activeColumn.id] || [];
    
    return (
      <Box>
        {/* Column Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              minWidth: 'auto',
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              mr: 0.75,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
            },
            '& .Mui-selected': {
              bgcolor: 'primary.50',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {columns.map((column, index) => (
            <Tab
              key={column.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: column.color,
                    }}
                  />
                  <span>{column.title.split(' ')[0]}</span>
                  <Badge
                    badgeContent={(tasksByStatus[column.id] || []).length}
                    color={index === activeTab ? 'primary' : 'default'}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        minWidth: 16,
                        height: 16,
                        right: -8,
                      },
                    }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Task Cards */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={activeColumn.id}>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  minHeight: 200,
                  bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  borderRadius: 2,
                  p: 0.5,
                  transition: 'background-color 0.2s ease',
                }}
              >
                {columnTasks.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2">
                      No tasks in {activeColumn.title.toLowerCase()}
                    </Typography>
                  </Box>
                ) : (
                  columnTasks.map((task, index) => {
                    const isTimerActive = activeTimers.some((t) => t.taskId === task.id);

                    return (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => handleTaskClick(task.id)}
                            sx={{
                              p: 1.5,
                              mb: 1.5,
                              cursor: 'pointer',
                              bgcolor: isTimerActive ? 'success.50' : 'background.paper',
                              borderLeft: '3px solid',
                              borderLeftColor: isTimerActive
                                ? 'success.main'
                                : activeColumn.color,
                              boxShadow: snapshot.isDragging
                                ? '0 4px 20px rgba(0,0,0,0.15)'
                                : '0 1px 3px rgba(0,0,0,0.08)',
                              transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                              transition: 'all 0.15s ease-in-out',
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }}
                          >
                            {/* Header: Priority + Timer */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 0.75,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriorityBadge priority={task.priority} size="small" />
                                {task.category && (
                                  <Chip
                                    size="small"
                                    label={getCategoryStyle(task.category).label}
                                    sx={{
                                      height: 18,
                                      fontSize: '0.6rem',
                                      bgcolor: getCategoryStyle(task.category).bgcolor,
                                      color: getCategoryStyle(task.category).color,
                                      '& .MuiChip-label': { px: 0.5 },
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {task.status !== TaskStatus.COMPLETED && canUseTimer(task) && (
                                  <Tooltip
                                    title={
                                      isTimerActive
                                        ? 'Timer active'
                                        : todaysPickedTaskIds.has(task.id)
                                          ? 'Start timer'
                                          : "Pick in Today's Tasks first"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleStartTimer(e, task.id, task.title)}
                                        disabled={isTimerActive || !todaysPickedTaskIds.has(task.id)}
                                        sx={{
                                          p: 0.5,
                                          bgcolor: isTimerActive ? 'success.100' : 'primary.50',
                                        }}
                                      >
                                        <Play
                                          size={12}
                                          fill={isTimerActive ? '#16a34a' : '#2563eb'}
                                          style={{ color: isTimerActive ? '#16a34a' : '#2563eb' }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                              </Box>
                            </Box>

                            {/* Task Reference */}
                            {task.taskRef && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.65rem', display: 'block', mb: 0.25 }}
                              >
                                {task.taskRef}
                              </Typography>
                            )}

                            {/* Title */}
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{
                                mb: 0.75,
                                textDecoration:
                                  task.status === TaskStatus.COMPLETED
                                    ? 'line-through'
                                    : 'none',
                                color:
                                  task.status === TaskStatus.COMPLETED
                                    ? 'text.secondary'
                                    : 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: 1.3,
                              }}
                            >
                              {task.title}
                            </Typography>

                            {/* Footer: Due Date + Assignee */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Clock size={12} style={{ color: '#94a3b8' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: getDeadlineColor(task.dueDate, task.status),
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  {task.dueDate && dayjs(task.dueDate).isValid() ? dayjs(task.dueDate).format('MMM D') : '—'}
                                </Typography>
                              </Box>
                              {task.raci.responsible[0] && (
                                <Tooltip title={task.raciNames?.responsible?.[0] || task.raci.responsible[0]}>
                                  <Avatar
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      fontSize: '0.6rem',
                                      bgcolor: 'primary.main',
                                    }}
                                  >
                                    {(task.raciNames?.responsible?.[0] || task.raci.responsible[0]).charAt(0).toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              )}
                            </Box>
                          </Card>
                        )}
                      </Draggable>
                    );
                  })
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        {/* Dependency Warning Snackbar */}
        <Snackbar
          open={blockingWarning.open}
          autoHideDuration={8000}
          onClose={handleCloseWarning}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: 80 }}
        >
          <Alert
            severity="warning"
            icon={<Lock size={18} />}
            onClose={handleCloseWarning}
            sx={{ width: '100%', fontSize: '0.8rem' }}
            action={
              <Button color="inherit" size="small" onClick={handleViewBlockingTasks}>
                View
              </Button>
            }
          >
            {blockingWarning.message}
          </Alert>
        </Snackbar>

        <Snackbar
          open={Boolean(timerPickError)}
          autoHideDuration={5000}
          onClose={() => setTimerPickError('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: 140 }}
        >
          <Alert severity="error" onClose={() => setTimerPickError('')} sx={{ width: '100%' }}>
            {timerPickError}
          </Alert>
        </Snackbar>

        <ConfirmationModal
          open={!!confirmCompleteTask}
          onClose={() => setConfirmCompleteTask(null)}
          data={
            confirmCompleteTask
              ? {
                  title: 'Mark as completed',
                  message: 'Mark this task as completed?',
                  confirmText: 'Mark completed',
                  type: 'success',
                  onConfirm: () => {
                    if (confirmCompleteTask) {
                      dispatch(clearTimerForTask(confirmCompleteTask.id));
                      dispatch(updateTaskStatus({ taskId: confirmCompleteTask.id, status: TaskStatus.COMPLETED }));
                      onStatusChange?.(confirmCompleteTask.id, TaskStatus.COMPLETED);
                      taskService.completeTask(confirmCompleteTask.id).then(() => {
                        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
                        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
                      });
                      setConfirmCompleteTask(null);
                    }
                  },
                }
              : undefined
          }
        />
      </Box>
    );
  }

  // Desktop Kanban View
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflow: 'auto',
          pb: 2,
          minHeight: 'calc(100vh - 280px)',
        }}
      >
        {columns.map((column) => {
          const columnTasks = tasksByStatus[column.id] || [];

          return (
            <Droppable key={column.id} droppableId={column.id}>
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minWidth: 300,
                    maxWidth: 300,
                    bgcolor: snapshot.isDraggingOver
                      ? 'action.hover'
                      : 'background.default',
                    borderRadius: 2,
                    p: 2,
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {/* Column Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: column.color,
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {column.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={columnTasks.length}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    />
                  </Box>

                  {/* Tasks */}
                  <Box sx={{ minHeight: 100 }}>
                    {columnTasks.map((task, index) => {
                      const isTimerActive = activeTimers.some(t => t.taskId === task.id);

                      return (
                        <Draggable
                          key={task.id}
                          draggableId={task.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => handleTaskClick(task.id)}
                              sx={{
                                p: 2,
                                mb: 1.5,
                                cursor: 'pointer',
                                bgcolor: isTimerActive
                                  ? 'success.50'
                                  : 'background.paper',
                                borderLeft: isTimerActive
                                  ? '3px solid'
                                  : '3px solid transparent',
                                borderLeftColor: isTimerActive
                                  ? 'success.main'
                                  : 'transparent',
                                boxShadow: snapshot.isDragging
                                  ? '0 4px 20px rgba(0,0,0,0.15)'
                                  : '0 1px 3px rgba(0,0,0,0.08)',
                                transform: snapshot.isDragging
                                  ? 'rotate(3deg)'
                                  : 'none',
                                '&:hover': {
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                },
                              }}
                            >
                              {/* Priority & Timer */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <PriorityBadge
                                  priority={task.priority}
                                  size="small"
                                />
                                {task.status !== TaskStatus.COMPLETED && canUseTimer(task) && (
                                  <Tooltip
                                    title={
                                      isTimerActive
                                        ? 'Timer active'
                                        : todaysPickedTaskIds.has(task.id)
                                          ? 'Start timer'
                                          : "Pick in Today's Tasks first"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) =>
                                          handleStartTimer(e, task.id, task.title)
                                        }
                                        disabled={isTimerActive || !todaysPickedTaskIds.has(task.id)}
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          bgcolor: isTimerActive
                                            ? 'success.100'
                                            : 'primary.50',
                                        }}
                                      >
                                        <Play
                                          size={12}
                                          fill={
                                            isTimerActive ? '#16a34a' : '#2563eb'
                                          }
                                          style={{
                                            color: isTimerActive
                                              ? '#16a34a'
                                              : '#2563eb',
                                          }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>

                              {/* Parent Task Link (if subtask) */}
                              {task.parentTaskId && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5, 
                                    mb: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': { '& .parent-link': { color: 'primary.main', textDecoration: 'underline' } }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/tasks?taskId=${task.parentTaskId}`);
                                  }}
                                >
                                  <ArrowUpRight size={10} style={{ color: '#94a3b8' }} />
                                  <Typography 
                                    className="parent-link"
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}
                                  >
                                    Subtask of: {tasks.find(t => t.id === task.parentTaskId)?.title?.substring(0, 20) || 'Parent'}...
                                  </Typography>
                                </Box>
                              )}

                              {/* Category & Issue Type Badges */}
                              {(task.category || (task.isIssue && task.issueType)) && (
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                                  {task.isIssue && task.issueType && (
                                    <Chip
                                      size="small"
                                      icon={getIssueTypeIcon(task.issueType)}
                                      label={getIssueTypeStyle(task.issueType).label}
                                      sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        bgcolor: getIssueTypeStyle(task.issueType).bgcolor,
                                        color: getIssueTypeStyle(task.issueType).color,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                      }}
                                    />
                                  )}
                                  {task.category && (
                                    <Chip
                                      size="small"
                                      icon={<Tag size={10} />}
                                      label={getCategoryStyle(task.category).label}
                                      sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        bgcolor: getCategoryStyle(task.category).bgcolor,
                                        color: getCategoryStyle(task.category).color,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                      }}
                                    />
                                  )}
                                </Box>
                              )}

                              {/* Task Reference */}
                              {task.taskRef && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}
                                >
                                  {task.taskRef}
                                </Typography>
                              )}

                              {/* Title */}
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  mb: 1,
                                  textDecoration:
                                    task.status === TaskStatus.COMPLETED
                                      ? 'line-through'
                                      : 'none',
                                  color:
                                    task.status === TaskStatus.COMPLETED
                                      ? 'text.secondary'
                                      : 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {task.title}
                              </Typography>

                              {/* Subtask & Dependency Indicators */}
                              {((task.subtaskIds && task.subtaskIds.length > 0) ||
                                (task.blockedByTaskIds && task.blockedByTaskIds.length > 0) ||
                                (task.blockingTaskIds && task.blockingTaskIds.length > 0)) && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mb: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  {task.subtaskIds && task.subtaskIds.length > 0 && (
                                    <Tooltip title={`${task.subtaskIds.length} subtask(s)`}>
                                      <Chip
                                        size="small"
                                        icon={<GitBranch size={10} />}
                                        label={task.subtaskIds.length}
                                        sx={{
                                          height: 18,
                                          fontSize: '0.6rem',
                                          bgcolor: 'primary.50',
                                          color: 'primary.main',
                                          '& .MuiChip-icon': { color: 'inherit' },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {task.blockedByTaskIds && task.blockedByTaskIds.length > 0 && (
                                    <Tooltip title={`Blocked by ${task.blockedByTaskIds.length} task(s)`}>
                                      <Chip
                                        size="small"
                                        icon={<AlertCircle size={10} />}
                                        label={task.blockedByTaskIds.length}
                                        sx={{
                                          height: 18,
                                          fontSize: '0.6rem',
                                          bgcolor: 'warning.50',
                                          color: 'warning.main',
                                          '& .MuiChip-icon': { color: 'inherit' },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {task.blockingTaskIds && task.blockingTaskIds.length > 0 && (
                                    <Tooltip title={`Blocking ${task.blockingTaskIds.length} task(s)`}>
                                      <Chip
                                        size="small"
                                        icon={<Link2 size={10} />}
                                        label={task.blockingTaskIds.length}
                                        sx={{
                                          height: 18,
                                          fontSize: '0.6rem',
                                          bgcolor: 'info.50',
                                          color: 'info.main',
                                          '& .MuiChip-icon': { color: 'inherit' },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}

                              {/* Start & Due Dates */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  mb: 1.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                {/* Start Date */}
                                {task.startDate && dayjs(task.startDate).isValid() && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Calendar size={12} style={{ color: '#94a3b8' }} />
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary' }}
                                    >
                                      {dayjs(task.startDate).format('MMM D')}
                                    </Typography>
                                  </Box>
                                )}
                                {/* Due Date */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Clock size={12} style={{ color: '#94a3b8' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: getDeadlineColor(
                                        task.dueDate,
                                        task.status
                                      ),
                                    }}
                                  >
                                    {task.dueDate && dayjs(task.dueDate).isValid() ? dayjs(task.dueDate).format('MMM D, YYYY') : '—'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Footer: Assignee & Metadata */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                {/* Assignee */}
                                {task.raci.responsible[0] && (
                                  <Tooltip title={task.raciNames?.responsible?.[0] || task.raci.responsible[0]}>
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: '0.7rem',
                                        bgcolor: 'primary.main',
                                      }}
                                    >
                                      {(task.raciNames?.responsible?.[0] || task.raci.responsible[0])
                                        .charAt(0)
                                        .toUpperCase()}
                                    </Avatar>
                                  </Tooltip>
                                )}

                                {/* Metadata */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  {(task.actualMinutes || 0) > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Clock
                                        size={12}
                                        style={{ color: '#94a3b8' }}
                                      />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {Math.round(task.actualMinutes || 0)}h
                                      </Typography>
                                    </Box>
                                  )}
                                  {task.attachments &&
                                    task.attachments.length > 0 && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                        }}
                                      >
                                        <Paperclip
                                          size={12}
                                          style={{ color: '#94a3b8' }}
                                        />
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {task.attachments.length}
                                        </Typography>
                                      </Box>
                                    )}
                                </Box>
                              </Box>
                            </Card>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                </Paper>
              )}
            </Droppable>
          );
        })}
      </Box>

      {/* Dependency Warning Snackbar */}
      <Snackbar
        open={blockingWarning.open}
        autoHideDuration={8000}
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          icon={<Lock size={20} />}
          onClose={handleCloseWarning}
          sx={{ 
            width: '100%',
            maxWidth: 500,
            alignItems: 'center',
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleViewBlockingTasks}
            >
              View
            </Button>
          }
        >
          {blockingWarning.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(timerPickError)}
        autoHideDuration={5000}
        onClose={() => setTimerPickError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setTimerPickError('')} sx={{ width: '100%' }}>
          {timerPickError}
        </Alert>
      </Snackbar>

      <ConfirmationModal
        open={!!confirmCompleteTask}
        onClose={() => setConfirmCompleteTask(null)}
        data={
          confirmCompleteTask
            ? {
                title: 'Mark as completed',
                message: 'Mark this task as completed?',
                confirmText: 'Mark completed',
                type: 'success',
                onConfirm: async () => {
                  if (confirmCompleteTask) {
                    // Stop timer first if running
                    const runningTimer = activeTimers.find(t => t.taskId === confirmCompleteTask.id);
                    if (runningTimer) {
                      await dispatch(stopTimerAsync({ taskId: confirmCompleteTask.id })).unwrap();
                    }
                    
                    dispatch(updateTaskStatus({ taskId: confirmCompleteTask.id, status: TaskStatus.COMPLETED }));
                    onStatusChange?.(confirmCompleteTask.id, TaskStatus.COMPLETED);
                    taskService.completeTask(confirmCompleteTask.id).then(() => {
                      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
                      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
                      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
                    });
                    setConfirmCompleteTask(null);
                  }
                },
              }
            : undefined
        }
      />
    </DragDropContext>
  );
};

export default TaskKanbanView;
